import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import type {
  CloudflareAccessApplication,
  CloudflareAccessPolicy,
} from "../shared/types";
import { splitList, usePatchDraft } from "./admin-model";
import { useAdminApi, useRefreshCloudflare } from "./admin-query";
import { EmptyState } from "./AdminUi";
import { errorMessage } from "./api";
import { CloudflarePolicyEditor } from "./CloudflarePolicyEditor";
import { Modal } from "./Modal";

interface AccessApplicationDraft {
  name: string;
  sessionDuration: string;
  identityProviders: string;
  instantAuth: boolean;
  launcherVisible: boolean;
}

function initialDraft(
  application: CloudflareAccessApplication,
): AccessApplicationDraft {
  return {
    name: application.name,
    sessionDuration: application.sessionDuration ?? "24h",
    identityProviders: application.allowedIdentityProviders.join(", "),
    instantAuth: application.instantAuth,
    launcherVisible: application.launcherVisible,
  };
}

export function CloudflareApplicationDialog({
  application,
  onClose,
}: {
  application: CloudflareAccessApplication;
  onClose: () => void;
}) {
  const adminApi = useAdminApi();
  const refresh = useRefreshCloudflare();
  const [draft, patchDraft] = usePatchDraft(() => initialDraft(application));
  const [editingPolicy, setEditingPolicy] = useState<
    CloudflareAccessPolicy | "new"
  >();

  async function saved() {
    await refresh();
    onClose();
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      adminApi(
        `/api/admin/cloudflare/applications/${encodeURIComponent(application.id)}/settings`,
        {
          method: "PUT",
          body: JSON.stringify({
            confirmation: application.id,
            name: draft.name.trim(),
            sessionDuration: draft.sessionDuration.trim(),
            allowedIdentityProviders: splitList(draft.identityProviders, ","),
            instantAuth: draft.instantAuth,
            launcherVisible: draft.launcherVisible,
          }),
        },
      ),
    onSuccess: saved,
  });
  const busy = saveMutation.isPending;
  const error = saveMutation.error
    ? errorMessage(saveMutation.error, "The application could not be updated.")
    : "";

  function saveSettings(event: FormEvent) {
    event.preventDefault();
    saveMutation.mutate();
  }

  return (
    <Modal
      closeDisabled={busy}
      eyebrow="Cloudflare Access"
      focusKey={editingPolicy ? "policy" : "settings"}
      onClose={onClose}
      title={
        editingPolicy
          ? editingPolicy === "new"
            ? "Create policy"
            : `Edit ${editingPolicy.name}`
          : application.name
      }
      titleId="cloudflare-application-dialog-title"
      wide
    >
      {editingPolicy ? (
        <CloudflarePolicyEditor
          application={application}
          onCancel={() => setEditingPolicy(undefined)}
          onDone={saved}
          policy={editingPolicy === "new" ? undefined : editingPolicy}
        />
      ) : (
        <form className="dialog__form" onSubmit={saveSettings}>
          <div className="dialog__body access-application-form">
            <section className="access-config-section">
              <div>
                <span className="eyebrow">Application settings</span>
                <p>
                  Changes are merged into the latest Cloudflare object so
                  destinations and unsupported settings remain intact.
                </p>
              </div>
              <div className="form-grid">
                <label>
                  <span>Name</span>
                  <input
                    data-autofocus
                    disabled={busy}
                    maxLength={120}
                    onChange={(event) =>
                      patchDraft({ name: event.target.value })
                    }
                    required
                    value={draft.name}
                  />
                </label>
                <label>
                  <span>Session duration</span>
                  <input
                    disabled={busy}
                    onChange={(event) =>
                      patchDraft({ sessionDuration: event.target.value })
                    }
                    placeholder="24h"
                    required
                    value={draft.sessionDuration}
                  />
                </label>
                <label className="form-grid__wide">
                  <span>Allowed identity provider IDs</span>
                  <input
                    disabled={busy}
                    onChange={(event) =>
                      patchDraft({ identityProviders: event.target.value })
                    }
                    placeholder="Comma-separated Cloudflare IdP IDs"
                    value={draft.identityProviders}
                  />
                  <small>
                    Instant Auth requires exactly one identity provider.
                  </small>
                </label>
                <label className="checkbox-line">
                  <input
                    checked={draft.instantAuth}
                    disabled={busy}
                    onChange={(event) =>
                      patchDraft({ instantAuth: event.target.checked })
                    }
                    type="checkbox"
                  />
                  <span>Apply Instant Auth</span>
                </label>
                <label className="checkbox-line">
                  <input
                    checked={draft.launcherVisible}
                    disabled={busy}
                    onChange={(event) =>
                      patchDraft({ launcherVisible: event.target.checked })
                    }
                    type="checkbox"
                  />
                  <span>Show in App Launcher</span>
                </label>
              </div>
            </section>

            <section className="access-config-section">
              <header className="access-policy-header">
                <div>
                  <span className="eyebrow">Policies</span>
                  <p>
                    Ordered rules, session overrides, justification, and
                    temporary approvals.
                  </p>
                </div>
                <button
                  className="button button--secondary button--compact"
                  disabled={busy}
                  onClick={() => setEditingPolicy("new")}
                  type="button"
                >
                  Add policy
                </button>
              </header>
              <ul className="access-policy-list">
                {application.policies.map((policy) => (
                  <li key={policy.id}>
                    <span className="policy-order">{policy.precedence}</span>
                    <span>
                      <strong>{policy.name}</strong>
                      <small>
                        {policy.decision}
                        {policy.sessionDuration
                          ? ` · ${policy.sessionDuration}`
                          : " · app session"}
                        {policy.temporaryAuthentication
                          ? " · approval"
                          : policy.purposeJustificationRequired
                            ? " · justification"
                            : ""}
                      </small>
                    </span>
                    <button
                      className="button button--quiet button--compact"
                      disabled={busy}
                      onClick={() => setEditingPolicy(policy)}
                      type="button"
                    >
                      Edit
                    </button>
                  </li>
                ))}
              </ul>
              {!application.policies.length && (
                <EmptyState
                  compact
                  hint="Access denies traffic until an allow policy exists."
                  title="No policies"
                />
              )}
            </section>
            {error && <div className="notice notice--error">{error}</div>}
          </div>
          <footer className="dialog__actions">
            <button
              className="button button--secondary"
              disabled={busy}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="button button--primary"
              disabled={busy}
              type="submit"
            >
              {busy ? "Saving…" : "Save application"}
            </button>
          </footer>
        </form>
      )}
    </Modal>
  );
}
